import axios from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { ApiClientError } from '../api/api-error';
import { getApiClient } from '../api/client';
import { rememberLastLoginTenantCode } from '../router/login-route.js';
import type { SessionUser } from '@axsolutionsdev/shared';
import { loginRequest, logoutRequest, refreshRequest, type LoginUser } from '../api/auth';

/**
 * 인증 사용자 표현.
 *
 * BE `LoginResponse.user` 와 1:1. (api/auth.ts 의 LoginUser 와 동일하지만 store
 * 사용자가 import 해서 쓰기 좋게 같은 형태를 re-export.)
 */
export type AuthUser = LoginUser;

/**
 * Login 시 노출할 수 있는 분류 가능 오류.
 *
 * docs/04 §5.2 의 메시지 정책을 그대로 따른다 — 잠금/일반 인증 실패 외에는
 * "현재 시점에서 로그인할 수 없습니다" 정도로 통일.
 */
export type LoginErrorKind =
  | 'invalid_credentials' // 401 — 아이디/비밀번호 불일치 또는 테넌트 부재
  | 'account_locked' // 403 — 계정 잠금
  | 'tenant_inactive' // 403 — 테넌트 비활성
  | 'user_inactive' // 403 — 사용자 비활성/삭제
  | 'bad_request' // 400 — 폼 누락 등
  | 'network' // 네트워크/서버 오류
  | 'unknown';

/**
 * 분류된 로그인 오류. throw 가능하도록 Error 를 상속한다.
 * (LoginPage.vue 가 catch 후 `instanceof LoginError` 또는 그냥 `.kind` 로 분기.)
 */
export class LoginError extends Error {
  readonly kind: LoginErrorKind;

  constructor(kind: LoginErrorKind, message: string) {
    super(message);
    this.name = 'LoginError';
    this.kind = kind;
  }
}

/**
 * 인증 스토어.
 *
 * 정책:
 * - Access Token 은 docs/04 §2.1 에 따라 메모리(only Pinia) 에 저장. 영속화 X.
 * - Refresh Token 은 BE 가 HttpOnly 쿠키로 관리하므로 FE 는 직접 다루지 않는다.
 * - permissions 배열은 RBAC 라운드(F.x) 에서 채운다. 본 라운드는 빈 배열 유지.
 *
 * E.2 (refresh) 가 도입되기 전이라 새로고침 시 세션이 끊긴다 (의도된 한시 정책).
 */
export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null);
  const user = ref<AuthUser | null>(null);
  const permissions = ref<string[]>([]);
  const roles = ref<string[]>([]);

  const isAuthenticated = computed<boolean>(
    () => accessToken.value !== null && user.value !== null,
  );

  /** docs/04 §3.1 로그인 플로우. 성공 시 store 에 토큰/사용자/권한 적재. */
  async function login(input: {
    username: string;
    password: string;
    tenantCode: string;
  }): Promise<void> {
    try {
      const result = await loginRequest(getApiClient(), input);
      accessToken.value = result.accessToken;
      user.value = result.user;
      roles.value = result.user.roles;
      permissions.value = result.user.permissions;
      rememberLastLoginTenantCode(result.user.tenantCode);
    } catch (err: unknown) {
      throw normalizeLoginError(err);
    }
  }

  /**
   * docs/04 §3.3 로그아웃.
   *  - remote=true (default): BE `POST /api/auth/logout` 호출 → refresh token revoke + 쿠키 clear.
   *  - remote=false: 401 인터셉터가 부르는 로컬-only 청소(이미 토큰이 무효).
   * 어떤 경로든 메모리 상태는 항상 정리한다.
   */
  async function logout(opts: { remote?: boolean } = {}): Promise<void> {
    const remote = opts.remote ?? true;
    if (remote) {
      try {
        await logoutRequest(getApiClient());
      } catch {
        // 서버가 죽거나 이미 만료된 경우에도 메모리는 비워야 한다.
      }
    }
    accessToken.value = null;
    user.value = null;
    permissions.value = [];
    roles.value = [];
  }

  /**
   * docs/04 §3.2 refresh — HttpOnly 쿠키 기반.
   *
   * 응답에는 새 access token 과 사용자 컨텍스트(login 응답과 동일 형상) 가 함께
   * 내려온다. 성공 시 store 의 accessToken/user/roles/permissions 를 모두 갱신해
   * 새로고침 후 세션을 즉시 복원한다 (라우터 가드/인터셉터 silent refresh 경로에서
   * 호출됨). 실패는 caller 가 logout 처리.
   */
  async function refresh(): Promise<void> {
    const result = await refreshRequest(getApiClient());
    accessToken.value = result.accessToken;
    user.value = result.user;
    roles.value = result.user.roles;
    permissions.value = result.user.permissions;
    rememberLastLoginTenantCode(result.user.tenantCode);
  }

  /** 인터셉터가 새 access token 을 직접 주입할 때 사용 (refresh 1회 합치기 경로). */
  function setAccessToken(token: string | null): void {
    accessToken.value = token;
  }

  /** 프로필 수정 등 부분 갱신. */
  function patchUser(partial: Partial<AuthUser>): void {
    if (!user.value) return;
    user.value = { ...user.value, ...partial };
  }

  /** change-password 응답 등 전체 SessionUser 동기화. */
  function applySessionUser(next: SessionUser): void {
    user.value = next;
    roles.value = next.roles;
    permissions.value = next.permissions;
  }

  return {
    accessToken,
    user,
    permissions,
    roles,
    isAuthenticated,
    login,
    logout,
    refresh,
    setAccessToken,
    patchUser,
    applySessionUser,
  };
});

// =============================================================================
// helpers
// =============================================================================

/** axios/ApiClientError → 사용자에게 보여줄 분류된 오류로 변환.
 *
 * R4.B.3 이후 client.ts 가 모든 4xx/5xx 응답을 ApiClientError 로 wrap 하므로
 * `code` 기반 분기를 우선 적용한다. axios.isAxiosError 분기는 envelope 이 아닌
 * 응답 (network error / 임시 BE downgrade 등) 을 위한 fallback. */
function normalizeLoginError(err: unknown): LoginError {
  if (err instanceof ApiClientError) {
    const code = err.code;
    const m = err.message;
    if (code === 'AUTH_INVALID_CREDENTIALS' || code === 'AUTH_TOKEN_INVALID') {
      return new LoginError('invalid_credentials', m);
    }
    if (code === 'AUTH_ACCOUNT_LOCKED') return new LoginError('account_locked', m);
    if (code === 'AUTH_USER_INACTIVE') {
      // BE 는 tenant 비활성도 USER_INACTIVE 코드로 묶어 보낸다 (정보 노출 최소화).
      // 메시지 본문으로 tenant vs user 구분 (사용자 노출 메시지는 동일 카테고리).
      if (m.includes('테넌트')) return new LoginError('tenant_inactive', m);
      return new LoginError('user_inactive', m || '현재 로그인할 수 없는 계정입니다.');
    }
    if (code === 'VALIDATION_FAILED') return new LoginError('bad_request', m);
    return new LoginError('unknown', m);
  }
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data as { message?: unknown } | undefined;
    const serverMessage = typeof data?.message === 'string' ? data.message : undefined;

    if (status === 400) {
      return new LoginError('bad_request', serverMessage ?? '입력값을 확인해 주세요.');
    }
    if (status === 401) {
      return new LoginError(
        'invalid_credentials',
        serverMessage ?? '아이디 또는 비밀번호가 올바르지 않습니다.',
      );
    }
    if (status === 403) {
      // BE 는 "계정이 일시적으로 잠겨 있습니다..." / "테넌트가 활성 상태가 아닙니다"
      // / "해당 계정은 사용할 수 없는 상태입니다" 셋을 보낸다. 메시지 본문으로 분기.
      const m = serverMessage ?? '';
      if (m.includes('잠겨')) return new LoginError('account_locked', m);
      if (m.includes('테넌트')) return new LoginError('tenant_inactive', m);
      return new LoginError('user_inactive', m || '현재 로그인할 수 없는 계정입니다.');
    }
    if (!err.response) {
      return new LoginError('network', '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.');
    }
    return new LoginError(
      'unknown',
      serverMessage ?? `로그인 중 오류가 발생했습니다 (HTTP ${status}).`,
    );
  }

  return new LoginError(
    'unknown',
    err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.',
  );
}
